import React, { MouseEvent, ReactElement, Ref } from 'react';
import { Button, IconButton, IconButtonProps, SxProps, Theme, ToggleButton } from '@mui/material';

import { OptionalTooltip } from '@paragon/material';

interface Props extends IconButtonProps {
  title: string;
  icon: ReactElement;

  sx?: SxProps<Theme>;

  clickableDuringPicking?: boolean;

  ref?: Ref<HTMLButtonElement | null>;

  /**
   * if not `undefined`, the button will be a toggle button with the given value
   */
  toggleValue?: boolean;

  visibleLabel?: boolean;

  disabled?: boolean;

  /**
   * if set, this value will be used for the tooltip instead of the `title`
   */
  tooltipOverride?: string;

  disableTooltip?: boolean;

  onClick?: (event: MouseEvent<HTMLElement>) => void;
}

export const TOOLBAR_BUTTON_BORDER_RADIUS = '5px';

export function ToolbarIconButton({
  title,
  icon,
  sx,
  clickableDuringPicking,
  ref,
  toggleValue,
  visibleLabel,
  disabled,
  tooltipOverride,
  disableTooltip,
  onClick,
  children,
  ...buttonProps
}: Props) {
  const commonProps = {
    ref,
    disabled,
    size: 'small',
    'aria-label': title,
    sx: [{ border: 0, borderRadius: TOOLBAR_BUTTON_BORDER_RADIUS }, ...(Array.isArray(sx) ? sx : [sx])],
  } as const;

  let content;

  if (toggleValue !== undefined) {
    content = (
      <ToggleButton
        {...commonProps}
        value="toggle" // required
        selected={toggleValue}
        onChange={(event) => {
          if (!disabled && onClick) {
            onClick(event);
          }
        }}
      >
        {icon}
        {visibleLabel === true && title}
        {children}
      </ToggleButton>
    );
  } else if (visibleLabel === true) {
    content = (
      <Button
        {...commonProps}
        startIcon={icon}
        onClick={(event) => {
          if (!disabled && onClick) {
            onClick(event);
          }
        }}
      >
        {title}
        {children}
      </Button>
    );
  } else {
    content = (
      <IconButton
        {...buttonProps}
        {...commonProps}
        onClick={(event) => {
          if (!disabled && onClick) {
            onClick(event);
          }
        }}
      >
        {icon}
        {children}
      </IconButton>
    );
  }

  return (
    <OptionalTooltip
      disableInteractive
      title={tooltipOverride ?? title}
      visible={disableTooltip !== true}
      {...(clickableDuringPicking && { className: 'clickable-during-picking' })}
    >
      {content}
    </OptionalTooltip>
  );
}
